import React, { useState } from 'react';
import { Product, TagDisplaySize } from '../types';
import { useStore } from '../context/StoreContext';
import { MessageCircle, Eye, ArrowUpRight, Sparkles, ZoomIn, X, Share2 } from 'lucide-react';
import { getContrastTextColor } from '../utils/tagColors';
import { Skeleton } from './Skeleton';
import {
  getCategoryLocalizedName,
  getVariantLocalizedName,
  getVariantOptionLocalizedName,
  getProductLocalizedTitle,
  getProductLocalizedBadge,
  getProductLocalizedTag,
  getProductPermalink
} from '../utils/localization';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { setSelectedProduct, formatPrice, filterByTagAndCategory, tagDisplaySize, language, t, showToast } = useStore();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isMagnified, setIsMagnified] = useState(false);

  const localizedTitle = getProductLocalizedTitle(product, language);
  const localizedBadge = getProductLocalizedBadge(product.badge, language);
  const productPermalink = getProductPermalink(product, language);

  const handleShareProduct = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const productUrl = `${window.location.origin}${productPermalink}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Coveristic | ${localizedTitle}`,
          text: language === 'ar'
            ? `شاهد ${localizedTitle} في استوديو كوفريستيك للطباعة حسب الطلب في لبنان`
            : `Check out ${localizedTitle} on Coveristic - Custom Print on Demand Studio in Lebanon`,
          url: productUrl,
        });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(productUrl);
      showToast(language === 'ar' ? 'تم نسخ رابط المنتج إلى الحافظة بنجاح!' : 'Product link copied to clipboard!');
    } catch {
      showToast(productUrl);
    }
  };

  const primaryVariantGroup = product.variants?.[0];

  // Consolidate all product tags (both string tags and custom styled tag)
  const allTagNames: { text: string; color?: string }[] = [];
  if (product.customTag?.text) {
    allTagNames.push({ text: product.customTag.text, color: product.customTag.color });
  }
  if (Array.isArray(product.customTags)) {
    product.customTags.forEach(ct => {
      if (ct?.text && !allTagNames.some(existing => existing.text.toLowerCase() === ct.text.toLowerCase())) {
        allTagNames.push({ text: ct.text, color: ct.color });
      }
    });
  }
  if (Array.isArray(product.tags)) {
    product.tags.forEach(t => {
      const trimmed = t.trim();
      if (trimmed && !allTagNames.some(existing => existing.text.toLowerCase() === trimmed.toLowerCase())) {
        allTagNames.push({ text: trimmed });
      }
    });
  }

  const tagCount = allTagNames.length;

  // Dynamic tag scaling based on user-selected tagDisplaySize:
  // Supports 'xs' (Compact), 'sm' (Small), 'md' (Standard - Default), 'lg' (Large - Prominent), 'xl' (Extra Large - Bold)
  const getTagClasses = (size: TagDisplaySize = 'md', count: number) => {
    switch (size) {
      case 'xs':
        return {
          badge: count <= 2 
            ? "text-[7.5px] sm:text-[9px] px-1.5 py-0.5 font-bold tracking-tight" 
            : "text-[7px] sm:text-[8px] px-1 py-0.25 font-bold tracking-tight",
          dot: "w-1 h-1",
          maxWidth: "max-w-[75px] sm:max-w-[95px]"
        };
      case 'sm':
        return {
          badge: count <= 2 
            ? "text-[8.5px] sm:text-[10px] px-2 py-0.5 font-bold tracking-tight" 
            : "text-[8px] sm:text-[9px] px-1.5 py-0.5 font-bold tracking-tight",
          dot: "w-1.5 h-1.5",
          maxWidth: "max-w-[90px] sm:max-w-[115px]"
        };
      case 'lg':
        return {
          badge: count <= 2 
            ? "text-[11.5px] sm:text-[13px] px-2.5 sm:px-3 py-1 font-extrabold tracking-wide" 
            : "text-[10.5px] sm:text-[12px] px-2 sm:px-2.5 py-0.5 font-bold tracking-normal",
          dot: "w-1.5 h-1.5",
          maxWidth: "max-w-[120px] sm:max-w-[150px]"
        };
      case 'xl':
        return {
          badge: count <= 2 
            ? "text-[13px] sm:text-[15px] px-3 sm:px-3.5 py-1 sm:py-1.5 font-black tracking-wide" 
            : "text-[11.5px] sm:text-[13.5px] px-2.5 py-1 font-extrabold tracking-normal",
          dot: "w-2 h-2",
          maxWidth: "max-w-[140px] sm:max-w-[180px]"
        };
      case 'md':
      default:
        return {
          badge: count <= 2 
            ? "text-[10px] sm:text-[11.5px] px-2 sm:px-2.5 py-0.5 font-bold tracking-tight" 
            : "text-[9px] sm:text-[10.5px] px-1.5 sm:px-2 py-0.5 font-bold tracking-tight",
          dot: "w-1.5 h-1.5",
          maxWidth: "max-w-[105px] sm:max-w-[135px]"
        };
    }
  };

  const tagStyles = getTagClasses(tagDisplaySize, tagCount);

  return (
    <>
      <div
        id={`product-card-${product.id}`}
        onClick={() => setSelectedProduct(product)}
        className="group cursor-pointer bg-white border border-neutral-200 hover:border-neutral-900 transition-all duration-200 rounded-sm flex flex-col justify-between overflow-hidden shadow-xs hover:shadow-md relative"
      >
        {/* Product Image Area - Clean, Minimal White Background with Skeleton Loader */}
        <div className="relative w-full aspect-square bg-white flex items-center justify-center p-3 sm:p-5 overflow-hidden border-b border-neutral-100">
          
          {/* Skeleton Shimmer placeholder while image loads */}
          {!isImageLoaded && (
            <div className="absolute inset-0 p-6 flex items-center justify-center bg-neutral-50 z-0">
              <Skeleton className="w-full h-full rounded-md opacity-80" />
            </div>
          )}

          {/* Responsive Badge & Tag Header - Guaranteed Zero Overlap */}
          <div className="absolute top-2 inset-x-2 z-10 flex items-start justify-between gap-1 pointer-events-none">
            {/* Badge Pill */}
            {localizedBadge ? (
              <span className="pointer-events-auto text-[10px] sm:text-xs font-black uppercase tracking-wider bg-[#111111] text-white px-2 sm:px-2.5 py-1 shadow-xs rounded-xs shrink-0 max-w-[50%] truncate">
                {localizedBadge}
              </span>
            ) : <span />}

            {/* Action Buttons: Share & Magnify */}
            <div className="flex items-center gap-1 pointer-events-auto">
              {/* Share Quick Button */}
              <button
                type="button"
                onClick={handleShareProduct}
                title={language === 'ar' ? 'مشاركة رابط المنتج' : 'Share this product link'}
                aria-label={`Share ${localizedTitle}`}
                className="bg-white/95 hover:bg-[#E21B24] hover:text-white text-neutral-700 p-1.5 sm:p-2 rounded-full shadow-md transition-all border border-neutral-300 flex items-center justify-center active:scale-90 cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>

              {/* Magnify Quick Button (visible on mobile tap and hover) */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMagnified(true);
                }}
                title="Click to view larger magnified design"
                aria-label="Magnify product image"
                className="bg-white/95 hover:bg-neutral-900 hover:text-white text-neutral-800 p-1.5 sm:p-2 rounded-full shadow-md transition-all border border-neutral-300 flex items-center justify-center active:scale-90 cursor-pointer"
              >
                <ZoomIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          {/* Image Protection Transparent Shield - Prevents long-press and right-click saving */}
          <div 
            className="image-shield cursor-pointer"
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              showToast(language === 'ar' ? 'الصور محمية بحقوق الطبع والنشر لـ Coveristic' : 'Images are copyright-protected by Coveristic');
            }}
            onDragStart={(e) => e.preventDefault()}
            onClick={(e) => {
              // On mobile, tap on image opens magnified view
              if (window.innerWidth < 768) {
                e.stopPropagation();
                setIsMagnified(true);
              }
            }}
          />

          {/* Discreet copyright security mark */}
          <span className="product-watermark">© Coveristic</span>

          {/* Image - Minimal White Background with explicit dimensions, skeleton transition & lazy loading */}
          <img
            src={product.image}
            alt={localizedTitle}
            width={400}
            height={400}
            loading="lazy"
            decoding="async"
            draggable={false}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDragStart={(e) => e.preventDefault()}
            onLoad={() => setIsImageLoaded(true)}
            onError={(e) => {
              setIsImageLoaded(true);
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect fill="%23f9fafb" width="400" height="400"/><path d="M160 220l30-40 40 50 25-30 35 45H110z" fill="%23e5e7eb"/><circle cx="160" cy="150" r="18" fill="%23e5e7eb"/><text fill="%239ca3af" font-family="sans-serif" font-size="14" font-weight="bold" x="50%" y="78%" text-anchor="middle">Coveristic</text></svg>';
            }}
            className={`w-full h-full object-contain transform group-hover:scale-105 transition-all duration-300 select-none relative z-1 pointer-events-none ${
              isImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />

          {/* Quick View Overlay on Hover (Desktop) */}
          <div className="hidden sm:flex absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 items-center justify-center pointer-events-none">
            <div className="bg-white text-neutral-900 text-xs sm:text-sm font-black uppercase px-5 py-2.5 flex items-center gap-2 shadow-lg tracking-wider transform translate-y-2 group-hover:translate-y-0 transition-transform">
              <Eye className="w-4 h-4 text-[#E21B24]" />
              <span>{language === 'ar' ? 'تخصيص وطلب' : 'Customize & Order'}</span>
            </div>
          </div>
        </div>

        {/* Product Content Details */}
        <div className="p-3.5 sm:p-4.5 flex flex-col flex-1 justify-between space-y-3 bg-white">
          <div>
            {/* Category */}
            <div className="flex items-center justify-between text-xs sm:text-sm font-bold text-neutral-500 uppercase tracking-wider mb-1">
              <span>{getCategoryLocalizedName(product.category, language)}</span>
            </div>

            {/*Product Title - Bigger font with clear readability and crawlable permalink*/}
            <h3
              data-product-title="true"
              className="text-sm sm:text-base md:text-lg font-bold normal-case text-neutral-900 line-clamp-2 leading-snug group-hover:text-[#E21B24] transition-colors"
            >
              <a
                href={productPermalink}
                onClick={(e) => {
                  if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey && e.button === 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedProduct(product);
                  }
                }}
                className="hover:underline focus:outline-none"
              >
                {localizedTitle}
              </a>
            </h3>

            {/* Multiple Tags Display - Dynamic Size adjusted by admin */}
            {tagCount > 0 && (
              <div className="flex flex-wrap gap-1 mt-2 items-center">
                {allTagNames.map((tagItem, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      filterByTagAndCategory(tagItem.text, product.category);
                    }}
                    title={language === 'ar' ? `تصفية حسب "${getProductLocalizedTag(tagItem.text, language)}"` : `Filter by "${tagItem.text}" in ${product.category}`}
                    className={`rounded-xs uppercase cursor-pointer transition-transform hover:scale-105 active:scale-95 flex items-center gap-1 shadow-xs border ${tagStyles.badge}`}
                    style={{
                      backgroundColor: tagItem.color || '#f3f4f6',
                      borderColor: tagItem.color ? 'transparent' : '#e5e7eb',
                      color: tagItem.color ? getContrastTextColor(tagItem.color) : '#374151',
                    }}
                  >
                    <span className={`rounded-full bg-current opacity-80 shrink-0 ${tagStyles.dot}`} />
                    <span className={`truncate ${tagStyles.maxWidth}`}>{getProductLocalizedTag(tagItem.text, language)}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Variants Summary */}
            {primaryVariantGroup && (
              <p className="text-xs sm:text-sm text-neutral-600 mt-1.5 line-clamp-1 font-medium">
                {getVariantLocalizedName(primaryVariantGroup.name, language)}:{' '}
                {primaryVariantGroup.options.slice(0, 3).map(opt => getVariantOptionLocalizedName(opt, language)).join('، ')}
                {primaryVariantGroup.options.length > 3
                  ? (language === 'ar' ? ` +${primaryVariantGroup.options.length - 3} خيارات` : ` +${primaryVariantGroup.options.length - 3} more`)
                  : ''}
              </p>
            )}
          </div>

          {/* Price & WhatsApp CTA */}
          <div className="pt-2.5 border-t border-neutral-100 flex items-center justify-between gap-2">
            <div>
              <div className="flex items-baseline gap-1.5 sm:gap-2">
                <span className="text-base sm:text-lg md:text-xl font-black text-black font-heading">
                  {formatPrice(product.price)}
                </span>
                {product.comparePrice && (
                  <span className="text-xs sm:text-sm text-neutral-400 line-through font-semibold">
                    ${product.comparePrice.toFixed(2)}
                  </span>
                )}
              </div>
              <span className="text-[11px] sm:text-xs text-emerald-700 font-bold block leading-none mt-1">
                {language === 'ar' ? 'متوفر للتسليم الفوري • لبنان' : 'In Stock • Lebanon'}
              </span>
            </div>

            {/* Order button */}
            <button
              id={`btn-order-whatsapp-${product.id}`}
              aria-label={`Customize and order ${product.title} on WhatsApp`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedProduct(product);
              }}
              className="flex items-center gap-1.5 bg-[#E21B24] group-hover:bg-[#c9141c] text-white px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-black uppercase tracking-wider rounded-sm transition-all active:scale-95 shadow-xs shrink-0 cursor-pointer"
              title="Order directly on WhatsApp"
            >
              <MessageCircle className="w-3.5 sm:w-4 h-3.5 sm:h-4 fill-white text-transparent shrink-0" />
              <span>{language === 'ar' ? 'اطلب' : 'Order'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Magnified Image Lightbox Modal - Dismisses when clicking outside the image */}
      {isMagnified && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsMagnified(false);
          }}
          className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 cursor-zoom-out animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-label="Magnified Product View"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-3xl max-h-[90vh] flex flex-col items-center cursor-default"
          >
            {/* Close button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsMagnified(false);
              }}
              className="absolute -top-12 right-0 bg-neutral-800/90 hover:bg-neutral-700 text-white p-2 rounded-full text-xs font-bold flex items-center justify-center shadow-lg transition-colors cursor-pointer"
              title="Close magnification"
            >
              <X className="w-5 h-5" />
            </button>

            {/* High-res Magnified Image with Protection Shield */}
            <div className="relative bg-white p-3 sm:p-5 rounded-lg shadow-2xl flex items-center justify-center max-h-[75vh] max-w-[90vw] overflow-hidden select-none">
              <div 
                className="image-shield cursor-default"
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  showToast(language === 'ar' ? 'الصور محمية بحقوق الطبع والنشر لـ Coveristic' : 'Images are copyright-protected by Coveristic');
                }}
                onDragStart={(e) => e.preventDefault()}
              />
              <span className="product-watermark">© Coveristic</span>
              <img
                src={product.image}
                alt={localizedTitle}
                draggable={false}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragStart={(e) => e.preventDefault()}
                className="max-h-[70vh] max-w-[85vw] object-contain select-none pointer-events-none"
              />
            </div>

            {/* Caption & Dismiss info */}
            <div className="mt-3 text-center">
              <h4 className="text-white font-bold text-sm sm:text-base">{localizedTitle}</h4>
              <p className="text-neutral-400 text-xs mt-0.5">
                {language === 'ar' ? 'انقر في أي مكان خارج الصورة للإغلاق' : 'Click or tap anywhere outside this image to close magnification'}
              </p>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMagnified(false);
                  setSelectedProduct(product);
                }}
                className="mt-2 inline-flex items-center gap-1.5 bg-[#E21B24] hover:bg-[#c9141c] text-white text-xs font-black uppercase px-4 py-1.5 rounded-sm shadow-md"
              >
                <span>{language === 'ar' ? 'تخصيص وطلب' : 'Customize & Order'}</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
